import type { ImportFile } from '@/app/ai/hooks/useImportFilesToGrid';
import type { ContextSegmentSizes } from '@/app/ai/utils/contextMeasurement';
import type { AITool, AIToolsArgs } from 'quadratic-shared/ai/specs/aiToolsSpec';
import type { AIModelKey, AISource, AIUsage, Chat, Content, Context } from 'quadratic-shared/typesAndSchemasAI';
import type { CategorizedEmptyChatPromptSuggestions } from '@/app/ai/hooks/useGetEmptyChatPromptSuggestions';

/**
 * Type for prompt suggestions extracted from AI tool calls
 */
export type PromptSuggestions = AIToolsArgs[AITool.UserPromptSuggestions]['prompt_suggestions'];

/**
 * State for tracking context usage from AI API responses
 */
export interface ContextUsageState {
  /** The latest usage data from the most recent API response */
  usage: AIUsage | undefined;
  /** Id of the chat the usage belongs to, so stale usage from another chat is never shown */
  chatId: string | undefined;
}

/**
 * One entry in the per-chat history of provider-reported usage data.
 *
 * Captures the AIUsage returned for a single API round so the UI and
 * regression tests can chart growth across a conversation.
 */
export interface ContextUsageHistoryEntry {
  usage: AIUsage;
  timestamp: number;
  source: AISource;
  modelKey: AIModelKey | undefined;
}

/**
 * Bounded history of usage entries. {@link CONTEXT_USAGE_HISTORY_LIMIT} caps
 * the size to prevent unbounded growth in long-running sessions.
 */
export type ContextUsageHistoryState = ContextUsageHistoryEntry[];

export const CONTEXT_USAGE_HISTORY_LIMIT = 100;

/**
 * Snapshot of the per-segment character/token breakdown from the most recent
 * call to ContextBuilder.buildContext. Drives the per-segment indicator UI and
 * developer tooling.
 */
export type ContextSegmentSizesState = ContextSegmentSizes | undefined;

/**
 * State for empty chat suggestions (categorized prompts shown when chat is empty)
 */
export interface EmptyChatSuggestionsState {
  suggestions: CategorizedEmptyChatPromptSuggestions | undefined;
  contextHash: string | undefined;
  loading: boolean;
  abortController: AbortController | undefined;
}

/**
 * State for prompt suggestions with abort controller
 */
export interface PromptSuggestionsState {
  abortController: AbortController | undefined;
  suggestions: PromptSuggestions;
}

/**
 * A single multiple-choice question asked of the user via the
 * `ask_user_question` tool. Derived from the tool schema so the shape stays
 * in sync with what the AI sends.
 */
export type UserQuestionItem = AIToolsArgs[AITool.AskUserQuestion]['questions'][number];

/**
 * The user's answer to a single question: the option ids they selected and/or
 * free text entered in the "Other" field.
 */
export interface UserQuestionAnswer {
  optionIds: string[];
  otherText: string;
}

/** Answers for one `ask_user_question` tool call, keyed by question id. */
export type UserQuestionAnswers = Record<string, UserQuestionAnswer>;

/**
 * An in-flight `ask_user_question` tool call. While this is set, the AI tool
 * loop is paused (awaiting `resolve`) and the chat renders the interactive
 * question UI. Calling `resolve` with the formatted answer text returns the
 * tool result to the model and lets the loop continue.
 */
export interface PendingUserQuestion {
  toolCallId: string;
  title: string | undefined;
  questions: UserQuestionItem[];
  resolve: (answerText: string) => void;
}

/**
 * State for PDF import operations
 */
export interface PdfImportState {
  abortController: AbortController | undefined;
  loading: boolean;
  /** Name of the PDF currently being imported (set while loading). */
  fileName?: string;
  /** Number of page-range extraction requests that have settled so far. */
  completedChunks?: number;
  /** Total extraction requests planned; grows when failed chunks are re-split and retried. */
  totalChunks?: number;
}

/**
 * State for web search operations
 */
export interface WebSearchState {
  abortController: AbortController | undefined;
  loading: boolean;
}

/**
 * State for import files to grid operations
 */
export interface ImportFilesToGridState {
  loading: boolean;
}

/**
 * State for tracking failing SQL connections
 */
export interface FailingSqlConnectionsState {
  uuids: string[];
  lastResetTimestamp: number;
}

/**
 * A prompt waiting in the queue to be executed after the current one finishes
 */
export interface QueuedPrompt {
  id: string;
  content: Content;
  context: Context;
  importFiles: ImportFile[];
  /** Epoch ms when the prompt was queued, used to measure queue wait time. */
  queuedAt?: number;
}

/**
 * Creates a new empty chat with a fresh timestamp
 */
export const createDefaultChat = (): Chat => ({
  id: '',
  name: '',
  lastUpdated: Date.now(),
  messages: [],
});
